/// <amd-module name="ServiceHooks/Web/Admin/Components/FormInput/FormInput" />
import { InputDataType, InputDescriptor, InputMode, InputValue } from "ServiceHooks/Web/Admin/Contracts/FormInput";
import * as Resources from "ServiceHooks/Web/Admin/Resources";
import { useObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { css } from "VSS/Platform/Layout";
import { Checkbox } from "VSSUI/CheckBox";
import { Dropdown, DropdownCallout, DropdownExpandableTextField } from "VSSUI/Dropdown";
import { EditableDropdown } from "VSSUI/EditableDropdown";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { ListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { Location } from "VSSUI/Utilities/Position";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import * as React from "react";
import { sanitizeLinks } from "../../Utilities";
import { IFormInputErrorsProps, IFormInputLabelProps, IFormInputProps, IFormInputTextFieldProps, IValidationResult } from "./FormInput.Props";

export const FormInput: React.FunctionComponent<IFormInputProps> = props => {
    const { inputDescriptor } = props;

    switch (inputDescriptor.inputMode) {
        case InputMode.TextBox:
            return <FormInputTextBox {...props} />;
        case InputMode.TextArea:
            return <FormInputTextBox {...props} multiline={true} />;
        case InputMode.PasswordBox:
            return <FormInputTextBox {...props} inputType="password" />;
        case InputMode.Combo:
            return <FormInputDropdown {...props} />;
        case InputMode.CheckBox:
            return <FormInputCheckbox {...props} />;
        default:
            return null;
    }
};

export const FormInputLabel: React.FunctionComponent<IFormInputLabelProps> = props => {
    const { inputDescriptor } = props;
    const required = inputDescriptor.validation && inputDescriptor.validation.isRequired;

    return (
        <div className="hooks-form-input-label flex-row">
            <div className={`hooks-form-input-label title-xs icon-margin ${required ? 'hooks-form-input-label--required' : ''}${!required ? 'hooks-form-input-label--optional' : ''}`}>{sanitizeLinks(inputDescriptor.name)}</div>
            {inputDescriptor.description && (
                <Link
                    ariaLabel={inputDescriptor.description}
                    disabled={true}
                    tooltipProps={{
                    anchorOffset: { horizontal: 0, vertical: -10 },
                    anchorOrigin: { horizontal: Location.center, vertical: Location.start },
                    tooltipOrigin: { horizontal: Location.center, vertical: Location.end },
                    showOnFocus: true,
                    text: inputDescriptor.description,
                }}
                >
                    <Icon iconName="Unknown" />
                </Link>
            )}
        </div>
    );
};

export const FormInputTextBox: React.FunctionComponent<IFormInputTextFieldProps> = props => {
    const { inputDescriptor, onValueChange, multiline = false, inputType = "text", validity, value } = props;

    const handleChange = (event: any, newValue: string) => {
        onValueChange && onValueChange(newValue, validateFormInput(inputDescriptor, newValue));
    };

    return (
        <FormItem
            className="hooks-form-input flex-stretch"
            error={!validity.valid}
            label={<FormInputLabel 
                        inputDescriptor={inputDescriptor}
                    />}
            message={<FormInputErrors validity={validity} />}
        >
            <TextField
                className={css(!validity.valid && "bolt-textfield-error")}
                multiline={multiline}
                value={value}
                onChange={handleChange}
                placeholder={inputDescriptor.valueHint}
                inputType={inputType}
                suffixIconProps={!validity.valid ? { iconName: "Error", className: "hooks-form-input-error" } : undefined}
            />
        </FormItem>
    );
};

export const FormInputDropdown: React.FunctionComponent<IFormInputProps> = props => {
    const { inputDescriptor, loading, onValueChange, validity, value } = props;

    const possibleValues = inputDescriptor.values.possibleValues || [];
    const initialListBoxItems: IListBoxItem<InputValue>[] = possibleValues.map(x => ({ id: x.value, data: x, text: x.displayValue || x.value }));
    const shouldAddInitialValue = value && !inputDescriptor.values.isLimitedToPossibleValues && !possibleValues.some(x => x.value === value);
    shouldAddInitialValue && initialListBoxItems.unshift({ id: value, text: value });
    const [listBoxItems, setListBoxItems] = useObservableArray<IListBoxItem<InputValue>>(initialListBoxItems);

    const [selection] = React.useState(() => {
        let initialSelectedItemIndex = initialListBoxItems.findIndex(x => x.id === value);
        initialSelectedItemIndex = initialSelectedItemIndex > -1 ? initialSelectedItemIndex : 0;
        return new ListSelection({
            selectedRanges: initialListBoxItems.length > 0 ? [{ beginIndex: initialSelectedItemIndex, endIndex: initialSelectedItemIndex }] : [],
            selectOnFocus: false
        });
    });

    React.useEffect(() => {
        const handleChange = (selectionRanges: ISelectionRange[]) => {
            if (
                !inputDescriptor.values.isLimitedToPossibleValues &&
                selectionRanges[0] &&
                listBoxItems.value[selectionRanges[0].beginIndex] &&
                onValueChange
            ) {
                const selectedItemId = listBoxItems.value[selectionRanges[0].beginIndex].id;
                onValueChange(selectedItemId, validateFormInput(inputDescriptor, selectedItemId));
            }
        };

        selection.subscribe(handleChange);
        return () => selection.unsubscribe(handleChange);
    }, []);

    React.useEffect(() => {
        const possibleValues = inputDescriptor.values.possibleValues || [];
        const newListBoxItems: IListBoxItem<InputValue>[] = possibleValues.map(x => ({ id: x.value, data: x, text: x.displayValue || x.value }));
        const shouldAddInitialValue = value && !inputDescriptor.values.isLimitedToPossibleValues && !possibleValues.some(x => x.value === value);
        shouldAddInitialValue && newListBoxItems.unshift({ id: value, text: value });
        
        setListBoxItems(newListBoxItems);

        if (listBoxItems.length > 0) {
            let index = listBoxItems.value.findIndex(x => x.id === value);
            index = index > -1 ? index : 0;
            selection.select(index);
        } else {
            selection.clear();
        }
    }, [inputDescriptor.values.possibleValues]);

    return (
        <FormItem
            className="hooks-form-input flex-stretch flex-start"
            error={!validity.valid}
            label={
                <FormInputLabel 
                    inputDescriptor={inputDescriptor}
                />
            }
            message={<FormInputErrors validity={validity} />}
        >
            <div className="flex-row">
                {inputDescriptor.values.isLimitedToPossibleValues ? (
                    <Dropdown
                        ariaLabel={inputDescriptor.name}
                        items={listBoxItems}
                        renderExpandable={props => {
                            return (
                                <DropdownExpandableTextField
                                    {...props}
                                    className={css(props.className, "flex-grow")}
                                    disabled={listBoxItems.length <= 1}
                                />
                            );
                        }}
                        renderCallout={props => {
                            return (
                                <DropdownCallout
                                    {...props}
                                    anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                                    dropdownOrigin={{ horizontal: Location.start, vertical: Location.start }}
                                />
                            );
                        }}
                        selection={selection}
                    />
                ) : (
                    <EditableDropdown
                        allowFreeform={true}
                        className="flex-grow"
                        items={listBoxItems}
                        onValueChange={newValue => {
                            const newValueAsString = newValue && newValue.id;
                            onValueChange && onValueChange(newValueAsString, validateFormInput(inputDescriptor, newValueAsString));
                        }}
                        selection={selection}
                    />
                )}
                {loading && (
                    <Observer loading={loading}>
                        {(observerProps: { loading: boolean }) => {
                            return observerProps.loading ? (
                                <Spinner className={"hooks-form-input-inline-loading-spinner"} size={SpinnerSize.small} />
                            ) : null;
                        }}
                    </Observer>
                )}
            </div>
        </FormItem>
    );
};

export const FormInputCheckbox: React.FunctionComponent<IFormInputProps> = props => {
    const { inputDescriptor, onValueChange, value } = props;

    const handleChange = (event: any, checked: boolean) => {
        const stringValue = checked ? "true" : "";
        onValueChange && onValueChange(stringValue, validateFormInput(inputDescriptor, stringValue));
    };

    return (
        <Observer checkedAsString={value}>
            {(observerProps: { checkedAsString: string }) => (
                <Checkbox
                    className="hooks-form-input flex-start"
                    label={inputDescriptor.name}
                    checked={observerProps.checkedAsString === "true"}
                    onChange={handleChange}
                />
            )}
        </Observer>
    );
};

export const FormInputErrors: React.FunctionComponent<IFormInputErrorsProps> = props => {
    const { validity } = props;

    if (validity.errors.length === 0 || validity.errors.every(x => x.length == 0)) {
        return null;
    }

    return (
        <div className="hooks-form-input-errors flex-column">
            {validity.errors.map((errorString, index) => (
                <div className="hooks-form-input-error" key={`error-${index}`}>
                    {errorString}
                </div>
            ))}
        </div>
    );
};

const uriRegex = new RegExp("^https?://.+$");
const guidRegex = new RegExp("({){0,1}[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(}){0,1}");

const validResult: IValidationResult = { valid: true, errors: [] };
function invalidResult(message: string): IValidationResult {
    return { valid: false, errors: [message] };
}

export function validateFormInput(descriptor: InputDescriptor, value: string | undefined): IValidationResult {
    if (!descriptor.validation) {
        return validResult;
    }

    // Don't love this solution, but it's how the old system does it and things depend on it
    if (descriptor.isConfidential && value == "********") {
        return validResult;
    }

    if (!value) {
        if (descriptor.validation.isRequired) {
            return invalidResult("");
        } else {
            return validResult;
        }
    }

    if (descriptor.validation.isRequired) {
        if (value.length <= 0) {
            return invalidResult(Resources.ErrorRequired);
        }
    } else if (value.length === 0) {
        return validResult;
    }

    if (descriptor.validation.maxLength && value.length > descriptor.validation.maxLength) {
        return invalidResult(format(Resources.ErrorLengthTooLong, descriptor.validation.maxLength));
    }

    if (descriptor.validation.minLength && value.length < descriptor.validation.minLength) {
        return invalidResult(format(Resources.ErrorLengthTooShort, descriptor.validation.minLength));
    }

    if (descriptor.validation.pattern && !new RegExp(descriptor.validation.pattern).test(value)) {
        return invalidResult(descriptor.validation.patternMismatchErrorMessage || format(Resources.ErrorMatchPatten, descriptor.validation.pattern));
    }

    // Leave this last so it can return the test value
    if (descriptor.validation.dataType) {
        switch (descriptor.validation.dataType) {
            case InputDataType.Guid:
                return guidRegex.test(value) ? validResult : invalidResult(Resources.ErrorMatchGuid);
            case InputDataType.Uri:
                return uriRegex.test(value) ? validResult : invalidResult(Resources.ErrorMatchURL);
        }
    }

    return validResult;
}
